import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Scroll, Swords, Award, Globe } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { SECTIONS, createSectionVariants } from '../lib/motionDirector';

const About = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const stats = [
    { icon: Scroll, value: personalInfo.repositories, label: 'Repositories', kanji: '書' },
    { icon: Swords, value: '2+', label: 'Years Experience', kanji: '年' },
    { icon: Award, value: '8', label: 'Certifications', kanji: '証' },
    { icon: Globe, value: '2', label: 'Languages', kanji: '語' },
  ];

  // Motion Director orchestrates all movement
  const variants = createSectionVariants(SECTIONS.about);

  return (
    <section id="about" className="relative py-32 bg-zinc-950 overflow-hidden" ref={ref}>
      {/* Softer crimson-pink hue overlay - Scroll/paper aesthetic */}
      <div className="absolute inset-0 bg-gradient-to-tl from-rose-900/15 via-pink-950/5 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(244,63,94,0.08),transparent_50%)]" />
      {/* Kanji Typography Layer - Large watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-[25rem] font-bold text-amber-100/[0.03] select-none tracking-wider">序</div>
      </div>

      {/* Japanese background elements */}
      <div className="absolute inset-0">
        {/* Secondary kanji */}
        <div className="absolute top-10 right-10 text-[20rem] font-bold text-amber-100/[0.02] select-none">道</div>
        <div className="absolute bottom-10 left-10 text-[15rem] font-bold text-red-900/[0.03] select-none drop-shadow-[0_0_80px_rgba(220,38,38,0.35)]">魂</div>
        
        {/* Decorative circles (enso inspired) */}
        <motion.div
          className="absolute top-1/4 right-1/4 w-64 h-64 border border-amber-900/10 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-1/4 left-1/4 w-48 h-48 border border-red-900/10 rounded-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
        />
      </div>

      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        variants={variants.container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Header */}
        <motion.div
          variants={variants.item}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-red-700" />
            <span className="text-4xl text-red-800/30">旅</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-red-700" />
          </div>
          <span className="inline-block px-4 py-1 text-xs tracking-[0.3em] text-amber-500/80 border border-amber-900/40 rounded-full mb-4">
            THE JOURNEY
          </span>
          <h2 className="font-bebas text-5xl md:text-7xl tracking-wider text-amber-50">
            MY PATH AS A WARRIOR
          </h2>
        </motion.div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left - Summary */}
          <motion.div variants={variants.item}>
            {/* Scroll/Paper Aesthetic Container */}
            <div className="relative border-2 border-amber-900/40 bg-gradient-to-br from-zinc-900/50 to-black/50 rounded-lg p-8 backdrop-blur-sm">
              {/* Rolled scroll decorations at top and bottom */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-gradient-to-b from-amber-800/60 to-amber-900/80 rounded-full border border-amber-700/50" />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-gradient-to-t from-amber-800/60 to-amber-900/80 rounded-full border border-amber-700/50" />
              
              {/* Japanese vertical text decoration */}
              <div className="absolute -left-12 top-0 bottom-0 flex flex-col justify-center gap-4 text-red-800/40 text-lg font-bold" style={{ writingMode: 'vertical-rl' }}>
                <span>武</span>
                <span>士</span>
                <span>道</span>
              </div>
              
              {/* Shoji screen inspired border */}
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-crimson-700 via-amber-700 to-crimson-700" />
              <div className="absolute -right-4 top-0 bottom-0 w-1 bg-gradient-to-b from-crimson-700 via-amber-700 to-crimson-700" />
              <p className="text-amber-100/70 leading-relaxed text-lg relative z-10">
                {personalInfo.summary}
              </p>
              
              <div className="mt-8 relative z-10">
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-900/20 border border-green-700/40 rounded-full"
                  animate={{ boxShadow: ['0 0 0px rgba(34,197,94,0)', '0 0 20px rgba(34,197,94,0.3)', '0 0 0px rgba(34,197,94,0)'] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-400 text-sm tracking-wider">Available for {personalInfo.availability} Joining</span>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Right - Stats */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                variants={variants.item}
                className="group relative p-6 bg-zinc-900/30 border border-amber-900/20 rounded-lg hover:border-red-700/40 transition-all duration-500 overflow-hidden"
                whileHover={{ 
                  boxShadow: '0 20px 40px -15px rgba(139, 0, 0, 0.5), 0 0 0 2px rgba(251, 191, 36, 0.1)',
                  borderColor: 'rgba(185, 28, 28, 0.6)'
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Background kanji */}
                <span className="absolute -right-2 -bottom-4 text-6xl text-amber-100/[0.03] font-bold">{stat.kanji}</span>
                
                <motion.div
                  whileHover={{ x: 3, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <stat.icon className="w-8 h-8 text-red-700 mb-3" />
                  <div className="font-bebas text-5xl text-amber-50 mb-1">{stat.value}</div>
                  <div className="text-xs tracking-wider text-amber-100/40 uppercase">{stat.label}</div>
                </motion.div>
                
                <motion.div 
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-700 to-transparent"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default About;
